import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { fetchProduct, submitTeamOrder } from "../lib/api";
import { IMAGES } from "../data/site";
import useSeo from "../hooks/useSeo";

const initialForm = {
  club_name: "",
  contact_name: "",
  email: "",
  phone: "",
  num_players: 11,
  preferred_colourways: [],
  custom_colour_interest: false,
  required_delivery_date: "",
  notes: "",
};

const TeamOrdersPage = () => {
  const [form, setForm] = useState(initialForm);
  const [colourways, setColourways] = useState([]);
  const [loading, setLoading] = useState(false);

  useSeo({
    title: "Team Orders · Grip Socks for Academies, Clubs & Semi-Pro",
    description:
      "Bulk grip socks for football teams, academies and clubs. Club colour matching, bulk pricing and custom colourway interest forms.",
    path: "/team-orders",
  });

  useEffect(() => {
    fetchProduct()
      .then((p) => setColourways(p.colourways))
      .catch(() => {});
  }, []);

  const set = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const toggleColour = (slug) => {
    setForm((prev) => {
      const has = prev.preferred_colourways.includes(slug);
      return {
        ...prev,
        preferred_colourways: has
          ? prev.preferred_colourways.filter((c) => c !== slug)
          : [...prev.preferred_colourways, slug],
      };
    });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitTeamOrder({
        ...form,
        num_players: Number(form.num_players),
      });
      toast.success("Enquiry sent. Our team will be in touch within 2 working days.");
      setForm(initialForm);
    } catch {
      toast.error("Could not send. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="team-orders-page" className="bg-chalk">
      <section className="border-b border-line">
        <div className="container-x grid grid-cols-1 md:grid-cols-12 gap-8 py-16 md:py-24">
          <div className="md:col-span-7">
            <p className="overline mb-4">Team Orders</p>
            <h1 className="font-display uppercase text-5xl md:text-7xl tracking-tightest font-bold leading-[0.95]">
              Grip socks for teams,
              <br />
              academies <span className="text-mute">and clubs.</span>
            </h1>
            <p className="mt-6 max-w-xl text-mute leading-relaxed">
              Whether you are building out academy kit, supplying a semi-pro squad or giving a Sunday
              league team a sharper matchday setup, Pritch offers bulk order options across its core
              colour range.
            </p>
          </div>
          <div className="md:col-span-5">
            <div className="aspect-[4/3] border border-line overflow-hidden">
              <img src={IMAGES.teamOrders} alt="Footballs on pitch" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      <section className="container-x py-16 md:py-24">
        <form onSubmit={onSubmit} data-testid="team-orders-form" className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-5">
            <h2 className="font-display uppercase text-3xl md:text-4xl tracking-tightest font-bold leading-[0.95]">
              Bulk enquiry
            </h2>
            <p className="mt-4 text-mute text-sm leading-relaxed max-w-md">
              Tell us about your team and we'll come back with pricing, lead times and colour
              matching options.
            </p>
            <div className="mt-8 space-y-4 mono text-xs text-mute">
              <p>· Minimum 10 pairs</p>
              <p>· Club colour matching from 50 pairs</p>
              <p>· Custom colourways from 250 pairs</p>
              <p>· 2-week typical lead time</p>
            </div>
          </div>

          <div className="lg:col-span-7 bg-white border border-line p-6 md:p-10 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="overline block mb-2">Club / Academy name</label>
                <input
                  required
                  type="text"
                  value={form.club_name}
                  onChange={(e) => set("club_name", e.target.value)}
                  className="w-full border border-line px-4 py-3 bg-chalk focus:outline-none focus:border-ink"
                  data-testid="team-form-club-name"
                />
              </div>
              <div>
                <label className="overline block mb-2">Contact name</label>
                <input
                  required
                  type="text"
                  value={form.contact_name}
                  onChange={(e) => set("contact_name", e.target.value)}
                  className="w-full border border-line px-4 py-3 bg-chalk focus:outline-none focus:border-ink"
                  data-testid="team-form-contact-name"
                />
              </div>
              <div>
                <label className="overline block mb-2">Email</label>
                <input
                  required
                  type="email"
                  value={form.email}
                  onChange={(e) => set("email", e.target.value)}
                  className="w-full border border-line px-4 py-3 bg-chalk focus:outline-none focus:border-ink"
                  data-testid="team-form-email"
                />
              </div>
              <div>
                <label className="overline block mb-2">Phone (optional)</label>
                <input
                  type="text"
                  value={form.phone}
                  onChange={(e) => set("phone", e.target.value)}
                  className="w-full border border-line px-4 py-3 bg-chalk focus:outline-none focus:border-ink"
                  data-testid="team-form-phone"
                />
              </div>
              <div>
                <label className="overline block mb-2">Number of players</label>
                <input
                  required
                  type="number"
                  min="1"
                  value={form.num_players}
                  onChange={(e) => set("num_players", e.target.value)}
                  className="w-full border border-line px-4 py-3 bg-chalk focus:outline-none focus:border-ink"
                  data-testid="team-form-num-players"
                />
              </div>
              <div>
                <label className="overline block mb-2">Required delivery date</label>
                <input
                  type="date"
                  value={form.required_delivery_date}
                  onChange={(e) => set("required_delivery_date", e.target.value)}
                  className="w-full border border-line px-4 py-3 bg-chalk focus:outline-none focus:border-ink"
                  data-testid="team-form-delivery-date"
                />
              </div>
            </div>

            <div>
              <label className="overline block mb-3">Preferred colourways</label>
              <div className="flex flex-wrap gap-2">
                {colourways.map((c) => {
                  const active = form.preferred_colourways.includes(c.slug);
                  return (
                    <button
                      key={c.slug}
                      type="button"
                      onClick={() => toggleColour(c.slug)}
                      className={`inline-flex items-center gap-2 border px-3 py-2 text-xs transition-colors ${
                        active ? "bg-ink text-white border-ink" : "bg-chalk text-ink border-line hover:border-ink"
                      }`}
                      data-testid={`team-colour-${c.slug}`}
                    >
                      <span className="w-3 h-3 border border-current" style={{ backgroundColor: c.hex }} />
                      {c.name}
                    </button>
                  );
                })}
              </div>
            </div>

            <label className="flex items-center gap-3 text-sm">
              <input
                type="checkbox"
                checked={form.custom_colour_interest}
                onChange={(e) => set("custom_colour_interest", e.target.checked)}
                className="w-4 h-4 accent-ink"
                data-testid="team-form-custom"
              />
              Interested in custom club colours
            </label>

            <div>
              <label className="overline block mb-2">Notes</label>
              <textarea
                rows={4}
                value={form.notes}
                onChange={(e) => set("notes", e.target.value)}
                className="w-full border border-line px-4 py-3 bg-chalk focus:outline-none focus:border-ink"
                placeholder="Anything we should know about your club, kit or timing"
                data-testid="team-form-notes"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full"
              data-testid="team-form-submit"
            >
              {loading ? "Sending…" : "Send enquiry"}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
};

export default TeamOrdersPage;
